import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ujmsFilingQueue, ujmsFilingDetail, ujmsClaimFiling, ujmsAcceptFiling,
  ujmsRejectFiling, ujmsReturnFiling, ujmsOpenDocument, ujmsOpenFilingPdf, ujmsBulkFilings,
} from "../../services/ujmsApi";
import { Panel, Badge, thCls, tdCls, fmtDT, ageOf, btnPrimary, btnGhost, btnDanger, inputCls, cls } from "../ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { FileText, CheckCircle2, XCircle, Undo2 } from "lucide-react";

const FILTERS = [
  { key: "all", label: "All Pending", params: {} },
  { key: "unassigned", label: "Unassigned", params: { assigned: "unassigned", status: "SUBMITTED" } },
  { key: "mine", label: "My Queue", params: { assigned: "me" } },
  { key: "review", label: "Under Review", params: { status: "UNDER_REVIEW" } },
  { key: "accepted", label: "Accepted", params: { status: "FILED" } },
  { key: "rejected", label: "Rejected", params: { status: "REJECTED" } },
  { key: "today", label: "Today", params: { age: "today" } },
  { key: "older24", label: "Older than 24h", params: { age: "older24" } },
];

export default function FilingQueue() {
  const [filter, setFilter] = useState("all");
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(null);
  const [reason, setReason] = useState("");
  const [checks, setChecks] = useState({});
  const [sel, setSel] = useState({});
  const [bulkReason, setBulkReason] = useState("");

  const load = useCallback(async () => {
    const f = FILTERS.find((x) => x.key === filter);
    const params = { ...f.params };
    if (filter === "all") params.status = undefined;
    const r = await ujmsFilingQueue(params);
    let data = r.data;
    if (filter === "all") data = data.filter((x) => ["SUBMITTED", "UNDER_REVIEW"].includes(x.status));
    setRows(data);
    setSel({});
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const openFiling = async (id) => {
    const r = await ujmsFilingDetail(id);
    setOpen(r.data); setReason(""); setChecks({});
  };
  const [sp, setSp] = useSearchParams();
  const deepFiling = sp.get("filing");
  useEffect(() => {
    if (!deepFiling) return;
    openFiling(deepFiling).catch(() => toast.error("That filing could not be opened"));
    const next = new URLSearchParams(sp); next.delete("filing"); setSp(next, { replace: true });
  }, [deepFiling]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (fn, successMsg) => {
    try {
      await fn();
      toast.success(successMsg);
      setOpen(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };

  const CHECKLIST = ["Correct case and caption", "Document legible / complete", "Filing type matches document", "Confidentiality level appropriate"];
  const f = open?.filing;
  const reviewable = f && ["SUBMITTED", "UNDER_REVIEW", "RETURNED"].includes(f.status);

  const selectable = rows.filter((r) => ["SUBMITTED", "UNDER_REVIEW"].includes(r.status));
  const selectedIds = Object.keys(sel).filter((k) => sel[k]);
  const allSelected = selectable.length > 0 && selectable.every((r) => sel[r.id]);
  const toggleAll = () => {
    if (allSelected) { setSel({}); return; }
    const next = {};
    selectable.forEach((r) => { next[r.id] = true; });
    setSel(next);
  };
  const bulk = async (action) => {
    try {
      const r = await ujmsBulkFilings(selectedIds, action, action === "REJECT" ? bulkReason : "");
      const { succeeded, requested, results } = r.data;
      const skipped = results.filter((x) => x.status !== "OK").length;
      toast.success(`Bulk ${action.toLowerCase()}: ${succeeded}/${requested} processed${skipped ? ` (${skipped} skipped)` : ""}`);
      setBulkReason(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk action failed"); }
  };

  return (
    <div className="space-y-4" data-testid="filing-queue">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Filing Review Queue</h1>
        <p className="text-sm text-slate-500">Clerk review — accept onto the record, reject with reason, or return for correction.</p>
      </div>

      <div className="flex flex-wrap gap-1" data-testid="filing-filters">
        {FILTERS.map((x) => (
          <button key={x.key} onClick={() => setFilter(x.key)} data-testid={`filing-filter-${x.key}`}
            className={cls("text-xs font-semibold uppercase tracking-wide px-3 py-1.5 border rounded-sm",
              filter === x.key ? "bg-blue-900 text-white border-blue-900" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-100")}>
            {x.label}
          </button>
        ))}
      </div>

      {selectedIds.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 bg-blue-50 border border-blue-200 rounded-sm px-3 py-2" data-testid="bulk-toolbar">
          <span className="text-sm font-medium text-blue-900" data-testid="bulk-count">{selectedIds.length} selected</span>
          <button className={btnPrimary} onClick={() => bulk("ACCEPT")} aria-label="Accept all selected filings" data-testid="bulk-accept-btn">
            <CheckCircle2 className="w-3.5 h-3.5" /> Accept Selected
          </button>
          <input className={`${inputCls} w-72`} value={bulkReason} onChange={(e) => setBulkReason(e.target.value)}
            placeholder="Rejection reason (required for bulk reject)…" aria-label="Bulk rejection reason" data-testid="bulk-reason-input" />
          <button className={btnDanger} disabled={!bulkReason.trim()} onClick={() => bulk("REJECT")}
            aria-label="Reject all selected filings" data-testid="bulk-reject-btn">
            <XCircle className="w-3.5 h-3.5" /> Reject Selected
          </button>
          <button className={btnGhost} onClick={() => setSel({})} aria-label="Clear selection" data-testid="bulk-clear-btn">Clear</button>
        </div>
      )}

      <Panel testId="filing-table-panel">
        <table className="w-full border-collapse">
          <thead><tr>
            <th className={thCls}>
              <input type="checkbox" checked={allSelected} onChange={toggleAll} disabled={!selectable.length}
                aria-label="Select all reviewable filings" data-testid="bulk-select-all" />
            </th>
            <th className={thCls}>Received</th><th className={thCls}>Case Number</th><th className={thCls}>Case</th>
            <th className={thCls}>Filing</th><th className={thCls}>Filed By</th><th className={thCls}>Status</th>
            <th className={thCls}>Assigned Clerk</th><th className={thCls}>Age</th><th className={thCls}>Action</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-slate-50" data-testid={`filing-row-${r.id}`}>
                <td className={tdCls}>
                  {["SUBMITTED", "UNDER_REVIEW"].includes(r.status) && (
                    <input type="checkbox" checked={!!sel[r.id]} onChange={(e) => setSel({ ...sel, [r.id]: e.target.checked })}
                      aria-label={`Select filing ${r.filing_title || r.title}`} data-testid={`bulk-select-${r.id}`} />
                  )}
                </td>
                <td className={`${tdCls} font-mono text-xs whitespace-nowrap`}>{fmtDT(r.received_at)}</td>
                <td className={`${tdCls} font-mono text-xs`}>{r.case_number}</td>
                <td className={tdCls}><span className="line-clamp-1 max-w-[220px]">{r.caption}</span></td>
                <td className={tdCls}>{r.filing_title || r.title}<div className="text-xs text-slate-400">{r.filing_type}</div></td>
                <td className={`${tdCls} text-xs`}>{r.filed_by}</td>
                <td className={tdCls}><Badge value={r.status} /></td>
                <td className={`${tdCls} text-xs`}>{r.reviewed_by || "—"}</td>
                <td className={`${tdCls} font-mono text-xs`}>{ageOf(r.received_at)}</td>
                <td className={tdCls}><button className={btnGhost} onClick={() => openFiling(r.id)} aria-label={`Open filing ${r.filing_title || r.title}`} data-testid={`filing-open-${r.id}`}>Open</button></td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={10} className={tdCls}>No filings in this view.</td></tr>}
          </tbody>
        </table>
      </Panel>

      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="max-w-3xl" data-testid="filing-review-dialog">
          <DialogHeader><DialogTitle className="font-mono text-sm">{f?.filing_type} — {f?.filing_title}</DialogTitle></DialogHeader>
          {open && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Info label="Case" value={<Link className="text-blue-900 font-mono hover:underline" to={`/ujms/cases/${open.case?.id}`}>{open.case?.case_number}</Link>} />
                <Info label="Status" value={<Badge value={f.status} />} />
                <Info label="Filed By" value={f.filed_by} />
                <Info label="Received" value={fmtDT(f.received_at)} />
              </div>
              <div className="border border-slate-200 rounded-sm p-3 bg-slate-50">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Document</div>
                {open.document ? (
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-slate-400" />
                    <div className="flex-1">
                      <div className="font-medium">{open.document.title}</div>
                      <div className="text-xs text-slate-400">{open.document.filename} · {open.document.mime_type} · v{open.document.version}</div>
                    </div>
                    <button className={btnGhost} onClick={() => ujmsOpenDocument(open.document.id, open.document.title)} data-testid="filing-open-document-btn">Preview PDF</button>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-slate-400" />
                    <p className="text-xs text-slate-400 flex-1">Metadata-only filing — CJMS renders the registered record as a court PDF.</p>
                    <button className={btnGhost} onClick={() => ujmsOpenFilingPdf(f.id, f.filing_title || "Filing")} data-testid="filing-open-pdf-btn">Preview PDF</button>
                  </div>
                )}
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Parties</div>
                {(open.parties || []).map((p) => (
                  <span key={p.id} className="inline-flex items-center gap-1 mr-2 mb-1 text-xs border border-slate-200 rounded-sm px-2 py-0.5">
                    <Badge value={p.relationship_type} /> {p.display_name}
                  </span>
                ))}
              </div>
              {reviewable && (
                <>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Validation checklist</div>
                    {CHECKLIST.map((c) => (
                      <label key={c} className="flex items-center gap-2 text-sm py-0.5 cursor-pointer">
                        <input type="checkbox" checked={!!checks[c]} onChange={(e) => setChecks({ ...checks, [c]: e.target.checked })} data-testid={`filing-check-${c.split(" ")[0].toLowerCase()}`} />
                        {c}
                      </label>
                    ))}
                  </div>
                  <textarea className={inputCls} rows={2} placeholder="Reason (required for Reject / Return)…" value={reason}
                    onChange={(e) => setReason(e.target.value)} data-testid="filing-reason-input" />
                  <div className="flex gap-2 justify-end">
                    {!f.reviewed_by && <button className={btnGhost} onClick={() => act(() => ujmsClaimFiling(f.id), "Filing claimed to your queue")} data-testid="filing-claim-btn">Claim</button>}
                    <button className={btnGhost} disabled={!reason.trim()} onClick={() => act(() => ujmsReturnFiling(f.id, reason), "Filing returned for correction")} data-testid="filing-return-btn">
                      <Undo2 className="w-4 h-4" /> Return for Correction
                    </button>
                    <button className={btnDanger} disabled={!reason.trim()} onClick={() => act(() => ujmsRejectFiling(f.id, reason), "FILING_REJECTED")} data-testid="filing-reject-btn">
                      <XCircle className="w-4 h-4" /> Reject
                    </button>
                    <button className={btnPrimary} disabled={!CHECKLIST.every((c) => checks[c])} onClick={() => act(() => ujmsAcceptFiling(f.id), "FILING_ACCEPTED — entered on the docket")} data-testid="filing-accept-btn">
                      <CheckCircle2 className="w-4 h-4" /> Accept &amp; File
                    </button>
                  </div>
                </>
              )}
              {f?.status === "REJECTED" && <p className="text-xs text-red-700">Rejected: {f.rejection_reason}</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Info = ({ label, value }) => (
  <div><div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
    <div className="text-slate-800 mt-0.5">{value || "—"}</div></div>
);
